// J.I.R.T. तंत्र प्रशाला वाशी — 6-page Marathi school site (Forest Green + Amber + Cream)
import React, { useEffect, useState, useMemo, createContext, useContext } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InstituteSeo from "@/components/InstituteSeo";
import api from "@/lib/api";
import {
  Phone, Mail, MapPin, Menu, X, ArrowRight, GraduationCap, ShieldCheck,
  BookOpen, Cpu, Cog, Bot, FlaskConical, Wrench, Zap, Users, Award,
  Facebook, Instagram, Linkedin, Youtube, FileText, Send, Loader2,
  CheckCircle2, Trophy, Sparkles, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import GoverningBodySection from "@/components/GoverningBodySection";
import PublicAcademicsPage from "@/components/PublicAcademicsPage";
import RecentAcademicsWidget from "@/components/RecentAcademicsWidget";
import AlumniStrip from "@/components/AlumniStrip";

const SLUG = "jirt";
const Ctx = createContext(null);
export const useJirt = () => useContext(Ctx);

// --- Palette tokens (kept inline for clarity) ---
// Primary dark: #0F5132   Accent: #F59E0B   Cream bg: #FDFBF4
// Soft amber:   #FDE68A   Soft green: #D1FAE5

const NAV = [
  { to: "", end: true, label: "मुख्यपृष्ठ" },
  { to: "about", label: "आमच्याबद्दल" },
  { to: "gallery", label: "गॅलरी" },
  { to: "academics", label: "शैक्षणिक" },
  { to: "alumni", label: "माजी विद्यार्थी" },
  { to: "contact", label: "संपर्क" },
];

// Hardcoded JIRT staff contact roster (from the docx — not yet in CMS)
const CONTACT_ROSTER = [
  { name: "श्री. बामणे एस. डी.", phone: "8600586572", email: "BAMNEOMKAR@GMAIL.COM" },
  { name: "श्री. कीर्तिकर डी. बी.", phone: "8668298857", email: "KIRTIKARDAYANANAD2020@GMAIL.COM" },
  { name: "सौ. तर्मेकवाड आर. एन.", phone: "9021212635", email: "RNTARMEKWAD@gmail.com" },
  { name: "सौ. चेडे पी. जी.", phone: "7218352477", email: "POONAN211288@gmail.com" },
  { name: "श्री. माने ए. पी.", phone: "8888520157", email: "manepv@gmail.com" },
];

// Render HTML helper (supports plain Marathi text or rich HTML)
function renderHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml ? content : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

// ============================================================
//  LAYOUT
// ============================================================
export default function JirtLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/website/${SLUG}`);
        if (!cancel) setData(data);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "वेबसाइट कॉन्फिगर केलेली नाही");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => { window.scrollTo({ top: 0 }); setMobileOpen(false); }, [location.pathname]);

  if (loading) {
    return <div className="min-h-screen bg-[#FDFBF4] flex items-center justify-center text-[#0F5132] font-['Noto_Sans_Devanagari',sans-serif]" data-testid="jirt-loading"><Zap size={40} className="animate-pulse" /></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#FDFBF4] flex items-center justify-center px-4 font-['Noto_Sans_Devanagari',sans-serif] text-center" data-testid="jirt-error">
        <div>
          <Zap size={36} className="mx-auto text-[#0F5132]" />
          <h1 className="mt-4 text-2xl font-bold text-[#1c1a17]">वेबसाइट उपलब्ध नाही</h1>
          <p className="mt-2 text-sm text-[#475569]">{err || "कृपया नंतर पुन्हा प्रयत्न करा."}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute || {};
  const logo = inst.logo_url || "/assets/mitcsn-logo.png";

  return (
    <Ctx.Provider value={data}>
      <InstituteSeo slug="jirt" inst={inst} themeColor="#0F5132" />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700;800&family=Tiro+Devanagari+Marathi&display=swap" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-[#FDFBF4] text-[#1c1a17]" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}>
        {/* Top utility */}
        <div className="bg-[#0F5132] text-white/90 text-[12px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              {inst.email && <a href={`mailto:${inst.email}`} className="inline-flex items-center gap-1 hover:text-[#FDE68A]" data-testid="jirt-top-email"><Mail size={11} />{inst.email}</a>}
              {inst.address1 && <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-[#FDE68A]" />{inst.address1}</span>}
            </div>
            <div className="flex items-center gap-3">
              <InstituteSwitcher currentSlug="jirt" testidPrefix="jirt-top-switcher" />
              {inst.facebook_url && <a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FDE68A]"><Facebook size={12} /></a>}
              {inst.instagram_url && <a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FDE68A]"><Instagram size={12} /></a>}
              {inst.linkedin_url && <a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FDE68A]"><Linkedin size={12} /></a>}
              {inst.youtube_url && <a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FDE68A]"><Youtube size={12} /></a>}
              <span className="hidden sm:inline-flex items-center gap-1 bg-[#F59E0B] text-[#0F5132] px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-extrabold">
                <Sparkles size={10} /> इयत्ता ८ वी व ९ वी प्रवेश सुरू
              </span>
            </div>
          </div>
        </div>

        {/* Header band */}
        <header className="sticky top-0 z-40 bg-[#FDFBF4]/95 backdrop-blur border-b border-[#E5E0CC]">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-[auto_1fr_auto] items-center gap-4">
            <NavLink to="" end className="flex items-center" data-testid="jirt-logo">
              <img src={logo} alt={inst.name} className="h-[88px] sm:h-[104px] w-auto object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] sm:text-[11px] tracking-[0.18em] text-[#0F5132] font-semibold" data-testid="jirt-trust">
                ग्रामोद्योगिक शिक्षण मंडळ, छत्रपती संभाजीनगर संचालित
              </div>
              <div className="text-xl sm:text-3xl text-[#0F5132] font-extrabold mt-1" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }} data-testid="jirt-name">
                {inst.name || "जे.आय.आर.टी. तंत्र प्रशाला वाशी"}
              </div>
              <div className="text-[10px] sm:text-xs text-[#B45309] italic mt-1 tracking-wider">
                शासन मान्य अनुदानित संस्था · {inst.year_of_establishment || "१९७९"} पासून तांत्रिक शिक्षण
              </div>
            </div>
            <Link to="contact" className="hidden sm:inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F5132] hover:text-white font-extrabold text-xs uppercase tracking-wider px-4 py-2 rounded-md shadow-sm transition-colors" data-testid="jirt-cta-apply">
              <Zap size={14} /> आजच प्रवेश घ्या
            </Link>
          </div>

          {/* Main nav */}
          <nav className="bg-[#0F5132] shadow-md" data-testid="jirt-nav">
            <div className="max-w-7xl mx-auto px-4">
              <div className="hidden md:flex items-center gap-1 py-2 flex-wrap">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} data-testid={`jirt-nav-${n.to || "home"}`}
                    className={({ isActive }) => `text-sm px-4 py-2 rounded-md transition-colors font-semibold ${isActive ? "bg-[#F59E0B] text-[#0F5132]" : "text-white/95 hover:bg-white/10"}`}>
                    {n.label}
                  </NavLink>
                ))}
              </div>
              <div className="md:hidden flex items-center justify-between py-2">
                <span className="text-white font-semibold text-sm">मेन्यू</span>
                <button onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Toggle menu" data-testid="jirt-nav-toggle">{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
              </div>
              {mobileOpen && (
                <div className="md:hidden grid grid-cols-1 gap-1 pb-3">
                  {NAV.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `text-sm px-3 py-2 rounded-md ${isActive ? "bg-[#F59E0B] text-[#0F5132]" : "text-white/95 hover:bg-white/10"}`}>{n.label}</NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </header>

        {/* Scrolling announcement bar */}
        {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
          <JirtScrollerBar items={inst.scrollers} />
        )}

        <main className="flex-1"><Outlet /></main>

        {/* Footer */}
        <footer className="bg-[#0F5132] text-white/85 mt-12">
          <div className="max-w-7xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-[#FDE68A] uppercase tracking-[0.2em] text-xs font-bold">{inst.name || "जे.आय.आर.टी. तंत्र प्रशाला वाशी"}</div>
              <p className="mt-3 text-sm">ग्रामोद्योगिक शिक्षण मंडळ संचालित — पूर्व व्यवसायिक इलेक्ट्रिकल टेक्नॉलॉजी (कोड ९२) मधून कौशल्याधारित तांत्रिक शिक्षण.</p>
              <p className="mt-2 text-[11px] text-white/70 italic">&quot;कौशल्य, गुणवत्ता आणि आत्मनिर्भरतेकडे वाटचाल&quot;</p>
            </div>
            <div>
              <div className="text-white font-bold mb-3 text-sm">द्रुत दुवे</div>
              <ul className="space-y-2 text-sm">
                {NAV.map((n) => <li key={n.to}><NavLink to={n.to} end={n.end} className="hover:text-[#FDE68A]">{n.label}</NavLink></li>)}
              </ul>
            </div>
            <div>
              <div className="text-white font-bold mb-3 text-sm">संपर्क</div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 text-[#FDE68A] shrink-0" />{[inst.address1, inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(", ") || "वाशी ता-वाशी, जि-धाराशिव – ४१३५०३"}</li>
                <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 text-[#FDE68A]" /><a href={`mailto:${inst.email || "HMJIRT1234@GMAIL.COM"}`} className="hover:text-[#FDE68A]">{inst.email || "HMJIRT1234@GMAIL.COM"}</a></li>
                {(inst.contact_phone || inst.admission_phone) && <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 text-[#FDE68A]" />{[inst.admission_phone, inst.contact_phone].filter(Boolean).join(" · ")}</li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/70">© {new Date().getFullYear()} {inst.name || "जे.आय.आर.टी. तंत्र प्रशाला वाशी"} · ग्रामोद्योगिक शिक्षण मंडळ, छत्रपती संभाजीनगर. सर्व हक्क राखीव.</div>
        </footer>
      </div>
    </Ctx.Provider>
  );
}

// ============================================================
//  HOME PAGE
// ============================================================
export function JirtHome() {
  const data = useJirt();
  const inst = data.institute || {};
  const depts = data.departments || [];
  const staff = (data.staff || []).slice(0, 5);
  const defaultAbout = "आधुनिक तंत्रज्ञान, इलेक्ट्रिकल शिक्षण, रोबोटिक्स, संगणक शिक्षण आणि उद्योगाभिमुख प्रशिक्षणाच्या माध्यमातून विद्यार्थ्यांना भविष्यातील कुशल तंत्रज्ञ व उद्योजक बनविण्याचा आमचा प्रयत्न आहे. इयत्ता ८ वी व ९ वी साठी १००% निःशुल्क प्रवेश सुरू.";
  const aboutHtml = renderHtml((inst.about_institute || "").trim() || defaultAbout);

  const stats = [
    { v: "१००%", l: "निःशुल्क प्रवेश", icon: ShieldCheck },
    { v: "६० + ६०", l: "इ. ८ वी व ९ वी क्षमता", icon: Users },
    { v: inst.year_of_establishment || "१९७९", l: "पासून सेवा", icon: Award },
    { v: "कोड ९२", l: "इलेक्ट्रिकल टेक्नॉलॉजी", icon: Zap },
  ];

  const usp = [
    { icon: Zap, t: "इलेक्ट्रिकल टेक्नॉलॉजी", d: "पूर्व व्यवसायिक अभ्यासक्रम कोड ९२ — उद्योगाभिमुख विषय." },
    { icon: Bot, t: "रोबोटिक्स प्रशिक्षण", d: "STEM, Coding Basics, Mini Projects व Design Thinking." },
    { icon: Cpu, t: "संगणक शिक्षण", d: "डिजिटल कौशल्ये आणि भविष्यकालीन तंत्रज्ञानाची ओळख." },
    { icon: Wrench, t: "कौशल्य विकास", d: "व्यक्तिमत्त्व विकास, करिअर मार्गदर्शन व प्रात्यक्षिक अनुभव." },
  ];

  const facilities = [
    "१००% मोफत प्रवेश, पाठ्यपुस्तके व मध्यान्ह भोजन",
    "मोफत रोबोटिक्स प्रशिक्षण व संगणक शिक्षण",
    "आधुनिक विज्ञान व अत्याधुनिक विद्युत प्रयोगशाळा",
    "डिजिटल शिक्षण सुविधा व इंटरॅक्टिव्ह वर्ग",
    "सुसज्ज क्रीडांगण व मुला-मुलींसाठी स्वतंत्र स्वच्छतागृहे",
    "करिअर मार्गदर्शन, समुपदेशन व स्पर्धा परीक्षा मार्गदर्शन",
  ];

  return (
    <div data-testid="jirt-home">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#FDFBF4] via-[#FDE68A]/40 to-[#D1FAE5] overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#F59E0B]/25 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#0F5132]/15 blur-3xl" />
        {/* Subtle circuit grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(#0F5132 1px, transparent 1px), linear-gradient(90deg, #0F5132 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white text-[#0F5132] border border-[#0F5132]/25 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
              <GraduationCap size={14} /> इयत्ता ८ वी व ९ वी साठी प्रवेश सुरू
            </div>
            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }} data-testid="jirt-hero-heading">
              तंत्रज्ञान, गुणवत्ता आणि <span className="text-[#B45309]">उत्कृष्टता</span>
            </h1>
            <div className="mt-5 text-[#3a3a36] leading-relaxed prose prose-sm max-w-xl" data-testid="jirt-hero-about" dangerouslySetInnerHTML={{ __html: aboutHtml }} />
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="contact" className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F5132] hover:text-white font-bold px-6 py-3 rounded-md shadow-md transition-colors" data-testid="jirt-hero-cta">
                आजच प्रवेश घ्या <ArrowRight size={16} />
              </Link>
              <Link to="about" className="inline-flex items-center gap-2 border-2 border-[#0F5132] text-[#0F5132] hover:bg-[#0F5132] hover:text-white font-bold px-6 py-3 rounded-md transition-colors">
                अभ्यासक्रम पहा
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.l} className="bg-white border border-[#E5E0CC] rounded-xl p-3 sm:p-4 flex items-center gap-2 shadow-sm">
                    <Icon size={20} className="text-[#F59E0B] shrink-0" />
                    <div className="min-w-0">
                      <div className="font-extrabold text-[#0F5132] text-base leading-tight">{s.v}</div>
                      <div className="text-[10px] text-[#5b5650] leading-tight">{s.l}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            {(data.header_slides || [])[0]?.image_url ? (
              <img src={data.header_slides[0].image_url} alt={inst.name} className="w-full aspect-[5/4] object-cover rounded-2xl shadow-2xl ring-1 ring-[#E5E0CC]" />
            ) : inst.home_image_url ? (
              <img src={inst.home_image_url} alt={inst.name} className="w-full aspect-[5/4] object-cover rounded-2xl shadow-2xl ring-1 ring-[#E5E0CC]" />
            ) : (
              <div className="w-full aspect-[5/4] rounded-2xl shadow-2xl bg-gradient-to-br from-[#0F5132] via-[#0F5132] to-[#F59E0B] flex items-center justify-center relative overflow-hidden">
                <Zap size={140} className="text-white/30" strokeWidth={1} />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDE68A] font-bold">Pre-Vocational · Code 92</div>
                  <div className="text-lg font-bold leading-tight mt-1">Electrical Technology</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY TECHNICAL EDUCATION */}
      <section className="py-14 bg-white" data-testid="jirt-why">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] font-bold">Why Technical Education</div>
          <h2 className="mt-2 text-2xl sm:text-4xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>तांत्रिक शिक्षण का आवश्यक?</h2>
          <p className="mt-3 text-[#5b5650] max-w-2xl mx-auto">आजच्या आधुनिक युगात तांत्रिक ज्ञान ही काळाची गरज आहे. रोजगार, स्वयंरोजगार आणि उद्योजकतेकडे जाणारी थेट वाट.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {usp.map((u) => {
              const Icon = u.icon;
              return (
                <div key={u.t} className="bg-[#FDFBF4] border-2 border-[#E5E0CC] hover:border-[#F59E0B] hover:shadow-lg rounded-2xl p-6 text-center transition-all" data-testid={`jirt-usp-${u.t}`}>
                  <span className="inline-flex w-14 h-14 rounded-full bg-[#0F5132] text-[#FDE68A] items-center justify-center"><Icon size={24} /></span>
                  <h3 className="mt-3 font-bold text-[#0F5132] text-lg">{u.t}</h3>
                  <p className="mt-2 text-sm text-[#5b5650] leading-relaxed">{u.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COURSES & SUBJECTS */}
      <section className="py-14 bg-[#FDFBF4]" data-testid="jirt-courses">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] font-bold">Courses &amp; Subjects</div>
            <h2 className="mt-2 text-2xl sm:text-4xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>अभ्यासक्रम व विशेष प्रशिक्षण</h2>
            <p className="mt-2 text-[#5b5650]">पूर्व व्यवसायिक अभ्यासक्रम — इलेक्ट्रिकल टेक्नॉलॉजी (कोड ९२).</p>
          </div>
          {depts.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {depts.map((d, i) => {
                const iconList = [Zap, BookOpen, Bot, Cpu];
                const Icon = iconList[i % iconList.length];
                return (
                  <div key={d.id} data-testid={`jirt-course-${d.id}`} className="bg-white border-l-4 border-[#F59E0B] rounded-r-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                    <Icon size={22} className="text-[#0F5132]" />
                    <h3 className="mt-2 font-bold text-[#0F5132]">{d.name}</h3>
                    {d.code && <div className="mt-1 text-[10px] uppercase tracking-wider text-[#5b5650]">{d.code}</div>}
                    {d.about_html && <div className="mt-2 text-xs text-[#5b5650] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: d.about_html }} />}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { t: "तांत्रिक विषय", items: ["इलेक्ट्रिकल इंजिनिअरिंग ड्रॉईंग", "इलेक्ट्रिकल टेक्नॉलॉजी (कोड ९२)"], Icon: Zap },
                { t: "शालेय विषय", items: ["मराठी, हिंदी, इंग्रजी", "गणित, विज्ञान व तंत्रज्ञान", "सामाजिक शास्त्रे"], Icon: BookOpen },
                { t: "विशेष प्रशिक्षण", items: ["रोबोटिक्स प्रशिक्षण", "संगणक शिक्षण", "व्यक्तिमत्त्व विकास"], Icon: Bot },
                { t: "करिअर मार्गदर्शन", items: ["ITI – २५% आरक्षण", "MCVC – ४०% आरक्षण", "Polytechnic – १५% आरक्षण"], Icon: GraduationCap },
              ].map((c) => (
                <div key={c.t} className="bg-white border-l-4 border-[#F59E0B] rounded-r-xl p-5 shadow-sm">
                  <c.Icon size={22} className="text-[#0F5132]" />
                  <h3 className="mt-2 font-bold text-[#0F5132]">{c.t}</h3>
                  <ul className="mt-3 text-sm text-[#5b5650] space-y-1.5">{c.items.map((i) => <li key={i} className="flex items-start gap-1.5"><span className="text-[#F59E0B] mt-0.5">▸</span>{i}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ADMISSION */}
      <section className="py-14 bg-white" data-testid="jirt-admission">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] font-bold">Admissions</div>
            <h2 className="mt-2 text-2xl sm:text-4xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>प्रवेश प्रक्रिया</h2>
            <p className="mt-3 text-[#5b5650]">इयत्ता ७ वी उत्तीर्ण विद्यार्थ्यांना गुणवत्ता यादी (Merit) नुसार इयत्ता ८ वी व ९ वी मध्ये प्रवेश.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["१००% निःशुल्क प्रवेश", "तांत्रिक शिक्षण", "कौशल्य विकास", "आधुनिक प्रयोगशाळा", "करिअर मार्गदर्शन"].map((t) => (
                <span key={t} className="inline-block bg-[#D1FAE5] text-[#0F5132] text-xs font-bold px-3 py-1.5 rounded-full">{t}</span>
              ))}
            </div>
            <div className="mt-6 bg-[#FDE68A]/40 border border-[#F59E0B]/30 rounded-xl p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#B45309]">SSC नंतर सुवर्ण संधी</div>
              <p className="mt-1 text-sm text-[#5b4a20]">पूर्व व्यवसायिक अभ्यासक्रम उत्तीर्ण विद्यार्थ्यांसाठी ITI मध्ये २५%, MCVC मध्ये ४०% व Polytechnic मध्ये १५% आरक्षित जागा.</p>
            </div>
          </div>
          <div className="bg-[#FDFBF4] rounded-xl border-2 border-[#E5E0CC] overflow-hidden shadow-sm">
            <div className="bg-[#0F5132] text-white px-5 py-3 font-bold flex items-center gap-2"><Briefcase size={16} /> प्रवेश क्षमता</div>
            <table className="w-full text-sm">
              <thead className="bg-[#D1FAE5]"><tr><th className="text-left py-3 px-5 font-bold text-[#0F5132]">इयत्ता</th><th className="text-left py-3 px-5 font-bold text-[#0F5132]">पात्रता</th><th className="text-left py-3 px-5 font-bold text-[#0F5132]">क्षमता</th></tr></thead>
              <tbody>
                <tr className="border-t border-[#E5E0CC]"><td className="py-3 px-5">इ. ८ वी</td><td className="py-3 px-5 text-[#5b5650]">इ. ७ वी उत्तीर्ण</td><td className="py-3 px-5 font-bold text-[#B45309]">६०</td></tr>
                <tr className="border-t border-[#E5E0CC]"><td className="py-3 px-5">इ. ९ वी</td><td className="py-3 px-5 text-[#5b5650]">इ. ८ वी उत्तीर्ण</td><td className="py-3 px-5 font-bold text-[#B45309]">६०</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="py-14 bg-[#FDFBF4]" data-testid="jirt-facilities">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] font-bold">Facilities</div>
            <h2 className="mt-2 text-2xl sm:text-4xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>विद्यार्थ्यांसाठी सुविधा</h2>
            <p className="mt-2 text-[#5b5650]">विद्यार्थी-केंद्रित शिक्षण व्यवस्थेसाठी आवश्यक सुविधा.</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {facilities.map((f) => (
              <div key={f} className="bg-white border border-[#E5E0CC] rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[#0F5132] shrink-0 mt-0.5" />
                <span className="text-sm text-[#1c1a17]">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIAL FEATURES */}
      <section className="py-14 bg-white" data-testid="jirt-features">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] font-bold">Our Special Features</div>
            <h2 className="mt-2 text-2xl sm:text-4xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमची वैशिष्ट्ये</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "शालेय शिक्षणासोबत तांत्रिक शिक्षण", Icon: BookOpen },
              { t: "आधुनिक कार्यशाळा व प्रयोगशाळा", Icon: FlaskConical },
              { t: "रोबोटिक्स व कौशल्य प्रशिक्षण", Icon: Bot },
              { t: "उद्योगाभिमुख शिक्षण", Icon: Briefcase },
              { t: "रोजगार व स्वयंरोजगाराच्या संधी", Icon: Trophy },
              { t: "अनुभवी शिक्षक व तज्ज्ञ मार्गदर्शन", Icon: Users },
            ].map((f) => (
              <div key={f.t} className="bg-gradient-to-br from-[#FDFBF4] to-[#D1FAE5]/30 border-2 border-[#0F5132]/15 rounded-xl p-5 hover:border-[#F59E0B] hover:shadow-md transition-all">
                <f.Icon size={22} className="text-[#F59E0B]" />
                <p className="mt-2 text-[#0F5132] font-bold leading-snug">{f.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEACHERS */}
      {staff.length > 0 && (
        <section className="py-14 bg-[#FDFBF4]" data-testid="jirt-teachers">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-center text-2xl sm:text-4xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>शिक्षकवृंद</h2>
            <p className="mt-2 text-center text-[#5b5650]">अनुभवी शिक्षकांचे मार्गदर्शन आणि विद्यार्थी-केंद्रित शिक्षण.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {staff.map((s) => (
                <div key={s.id} className="bg-white rounded-xl p-4 ring-1 ring-[#E5E0CC] text-center hover:shadow-md transition-shadow">
                  {s.photo_url ? <img src={s.photo_url} alt={s.name} className="w-20 h-20 rounded-full object-cover mx-auto ring-2 ring-[#F59E0B]" /> : <div className="w-20 h-20 rounded-full bg-[#FDE68A] text-[#0F5132] mx-auto flex items-center justify-center"><Users size={28} /></div>}
                  <div className="mt-3 font-bold text-[#0F5132] text-sm">{s.name}</div>
                  {s.designation && <div className="text-[11px] text-[#5b5650]">{s.designation}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Strip */}
      <section className="py-12 bg-gradient-to-r from-[#0F5132] via-[#0F5132] to-[#065F46] text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #F59E0B 0%, transparent 40%), radial-gradient(circle at 80% 80%, #FDE68A 0%, transparent 40%)" }} />
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="text-[#FDE68A] tracking-[0.3em] text-[10px] uppercase font-bold">Motto · ब्रीदवाक्य</div>
          <h2 className="mt-2 text-2xl sm:text-4xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>&quot;तांत्रिक शिक्षणातून उज्ज्वल भविष्य&quot;</h2>
          <p className="mt-3 text-white/85 italic">कौशल्य, गुणवत्ता आणि आत्मनिर्भरतेकडे वाटचाल — १९७९ पासून सेवा.</p>
          <Link to="contact" className="mt-6 inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F5132] hover:text-white font-bold px-7 py-3 rounded-md shadow-lg transition-colors" data-testid="jirt-bottom-cta">
            प्रवेशासाठी संपर्क करा <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <RecentAcademicsWidget docs={data.academics || []} theme="saffron" testidPrefix="jirt-ac" linkTo="academics" eyebrow="शैक्षणिक" heading="अलीकडील दस्तऐवज" />
      <AlumniStrip items={(data.alumni || []).filter((a) => a.featured)} theme="school" eyebrow="माजी विद्यार्थी" heading="आमचे आजचे माजी विद्यार्थी" testid="jirt-alumni" registerHref="/jirt/alumni/register" />
    </div>
  );
}

// ============================================================
//  ABOUT PAGE
// ============================================================
export function JirtAbout() {
  const data = useJirt();
  const inst = data.institute || {};
  const leaders = data.leadership || [];
  const visionHtml = renderHtml((inst.vision || "").trim() || "१९७९ पासून गुणवत्तापूर्ण तांत्रिक शिक्षणाची परंपरा जपत, विद्यार्थ्यांना आधुनिक इलेक्ट्रिकल तंत्रज्ञानाचे ज्ञान, कौशल्ये व मूल्याधिष्ठित शिक्षण देऊन त्यांना सक्षम, आत्मनिर्भर, कुशल व जबाबदार नागरिक बनविणे.");
  const missionHtml = renderHtml((inst.mission || "").trim() || "पूर्व व्यवसायिक अभ्यासक्रम इलेक्ट्रिकल टेक्नॉलॉजी (कोड ९२) द्वारे विद्यार्थ्यांना गुणवत्तापूर्ण व कौशल्याधारित तांत्रिक शिक्षण प्रदान करणे. वैज्ञानिक दृष्टिकोन, नवोपक्रमशीलता व समस्या सोडविण्याची क्षमता विकसित करणे.");
  const aboutHtml = renderHtml((inst.about_institute || "").trim() || "जे.आय.आर.टी. तंत्र प्रशाला वाशी ही ग्रामोद्योगिक शिक्षण मंडळ, छत्रपती संभाजीनगर संचालित शासन मान्य अनुदानित संस्था असून १९७९ पासून वाशी ता-वाशी, जि-धाराशिव येथे पूर्व व्यवसायिक अभ्यासक्रम (इलेक्ट्रिकल टेक्नॉलॉजी कोड ९२) चालविते. शाळामान्यता क्र. MRO/TRQ/79-80/15286 दि. १७/०५/१९७९. शालांत परीक्षा मंडळ लातूर शाळा कोड क्र. ५९:०८:००३.");

  const trustFacts = [
    { label: "संस्था", value: "ग्रामोद्योगिक शिक्षण मंडळ, छत्रपती संभाजीनगर" },
    { label: "अभ्यासक्रम", value: "पूर्व व्यवसायिक — इलेक्ट्रिकल टेक्नॉलॉजी (कोड ९२)" },
    { label: "शाळामान्यता क्र.", value: "MRO/TRQ/79-80/15286 दि. १७/०५/१९७९" },
    { label: "शाळा कोड", value: "५९:०८:००३ (शालांत परीक्षा मंडळ, लातूर)" },
    { label: "यु-डायस क्र.", value: "२७२९०८०५८१०" },
    { label: "ईमेल", value: inst.email || "HMJIRT1234@GMAIL.COM" },
  ];

  return (
    <div data-testid="jirt-about">
      <section className="bg-gradient-to-br from-[#0F5132] via-[#0F5132] to-[#065F46] text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "linear-gradient(45deg, #FDE68A 25%, transparent 25%, transparent 75%, #FDE68A 75%), linear-gradient(45deg, #FDE68A 25%, transparent 25%, transparent 75%, #FDE68A 75%)", backgroundSize: "30px 30px", backgroundPosition: "0 0, 15px 15px" }} />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FDE68A] font-bold">About Us · आमची ओळख</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमच्याबद्दल</h1>
          <p className="mt-4 max-w-2xl text-white/85">ग्रामोद्योगिक शिक्षण मंडळ संचालित — कौशल्याधारित तांत्रिक शिक्षणाची भक्कम पायाभरणी, १९७९ पासून सेवा.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-2xl border border-[#E5E0CC] p-7 shadow-sm">
          <h2 className="text-2xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>शाळेबद्दल</h2>
          <div className="mt-4 prose prose-sm max-w-none text-[#1c1a17] leading-relaxed" data-testid="jirt-about-text" dangerouslySetInnerHTML={{ __html: aboutHtml }} />
          <div className="mt-6 border-t border-[#E5E0CC] pt-5">
            <div className="text-xs font-bold uppercase tracking-wider text-[#F59E0B] mb-3">संस्थेची माहिती</div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {trustFacts.map((f) => (
                <div key={f.label} className="flex flex-col">
                  <dt className="text-[11px] uppercase tracking-wider text-[#5b5650] font-semibold">{f.label}</dt>
                  <dd className="text-[#0F5132] font-medium">{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#0F5132] to-[#065F46] rounded-2xl p-6 text-white shadow-md">
            <div className="inline-flex items-center gap-2 bg-white/15 text-[#FDE68A] text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 rounded-sm">Vision · दृष्टी</div>
            <h3 className="mt-3 text-lg font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमची दृष्टी</h3>
            <div className="mt-2 text-sm prose prose-invert prose-sm max-w-none" data-testid="jirt-vision-text" dangerouslySetInnerHTML={{ __html: visionHtml }} />
          </div>
          <div className="bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-2xl p-6 text-[#0F5132] shadow-md">
            <div className="inline-flex items-center gap-2 bg-[#0F5132]/15 text-[#0F5132] text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 rounded-sm">Mission · ध्येय</div>
            <h3 className="mt-3 text-lg font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमचे ध्येय</h3>
            <div className="mt-2 text-sm prose prose-sm max-w-none text-[#0F5132]" data-testid="jirt-mission-text" dangerouslySetInnerHTML={{ __html: missionHtml }} />
          </div>
          <div className="bg-white rounded-2xl border-2 border-[#F59E0B]/40 p-6 shadow-sm">
            <div className="inline-flex items-center gap-2 bg-[#FDE68A] text-[#B45309] text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 rounded-sm">Motto · ब्रीदवाक्य</div>
            <p className="mt-3 text-[#0F5132] font-extrabold leading-snug" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>&quot;कौशल्य, गुणवत्ता आणि आत्मनिर्भरतेकडे वाटचाल&quot;</p>
            <p className="mt-2 text-xs text-[#5b5650] italic">Skill, Quality and Progress towards Self-Reliance</p>
          </div>
        </div>
      </section>

      {leaders.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-14">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F5132] mb-6" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>नेतृत्वाचा संदेश</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {leaders.map((l) => (
              <div key={l.id} className="bg-white border border-[#E5E0CC] rounded-2xl p-6 flex gap-5 items-start" data-testid={`jirt-leader-${l.id}`}>
                {l.photo_url ? <img src={l.photo_url} alt={l.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-[#FDE68A] shrink-0" /> : <div className="w-24 h-24 rounded-full bg-[#FDE68A] text-[#0F5132] flex items-center justify-center shrink-0"><Users size={32} /></div>}
                <div className="min-w-0 flex-1">
                  <div className="font-extrabold text-[#0F5132] text-lg">{l.name}</div>
                  {l.designation && <div className="text-xs uppercase tracking-wider text-[#F59E0B] font-bold">{l.designation}</div>}
                  {l.message_html && (
                    <div
                      className="mt-3 text-sm text-[#5b5650] leading-relaxed prose prose-sm max-w-none"
                      data-testid={`jirt-leader-msg-${l.id}`}
                      dangerouslySetInnerHTML={{ __html: renderHtml(l.message_html) }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 pb-14">
        <GoverningBodySection
          members={data.governing_body || []}
          theme="saffron"
          testidPrefix="jirt-gb"
          heading="संचालक मंडळ"
          eyebrow="Governing Body"
        />
      </section>
    </div>
  );
}

// ============================================================
//  GALLERY PAGE
// ============================================================
export function JirtGallery() {
  const data = useJirt();
  const photos = data.gallery || [];
  const [lb, setLb] = useState(-1);

  const types = useMemo(() => {
    const set = new Set();
    photos.forEach((p) => p.gallery_type && set.add(p.gallery_type));
    return Array.from(set);
  }, [photos]);
  const [filter, setFilter] = useState("");
  const visible = filter ? photos.filter((p) => p.gallery_type === filter) : photos;

  return (
    <div data-testid="jirt-gallery">
      <section className="bg-gradient-to-br from-[#0F5132] to-[#065F46] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FDE68A] font-bold">Gallery · क्षणचित्रे</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>कॅम्पस गॅलरी</h1>
          <p className="mt-4 max-w-2xl text-white/85">शाळा इमारत, विज्ञान व इलेक्ट्रिकल लॅब, रोबोटिक्स प्रशिक्षण, क्रीडांगण, औद्योगिक भेटी आणि उपक्रम.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        {types.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="jirt-gallery-filters">
            <button
              type="button"
              onClick={() => { setFilter(""); setLb(-1); }}
              data-testid="jirt-gallery-filter-all"
              className={`text-sm px-4 py-2 rounded-full transition-colors ${filter === "" ? "bg-[#0F5132] text-white font-semibold shadow-sm" : "bg-white text-[#0F5132] ring-1 ring-[#E5E0CC] hover:bg-[#FDE68A]/30"}`}
            >
              सर्व ({photos.length})
            </button>
            {types.map((t) => {
              const count = photos.filter((p) => p.gallery_type === t).length;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setFilter(t); setLb(-1); }}
                  data-testid={`jirt-gallery-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-sm px-4 py-2 rounded-full transition-colors ${filter === t ? "bg-[#F59E0B] text-[#0F5132] font-semibold shadow-sm" : "bg-white text-[#0F5132] ring-1 ring-[#E5E0CC] hover:bg-[#FDE68A]/30"}`}
                >
                  {t} ({count})
                </button>
              );
            })}
          </div>
        )}

        {visible.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center ring-1 ring-[#E5E0CC]" data-testid="jirt-gallery-empty">
            <FileText size={36} strokeWidth={1.2} className="mx-auto text-[#0F5132]/70" />
            <div className="mt-3 text-sm text-[#5b5650]">{filter ? `"${filter}" मध्ये अद्याप छायाचित्रे नाहीत.` : "लवकरच छायाचित्रे प्रकाशित केली जातील."}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="jirt-gallery-grid">
            {visible.map((g, i) => (
              <button
                key={g.id}
                onClick={() => setLb(i)}
                data-testid={`jirt-gallery-photo-${g.id}`}
                className="aspect-square overflow-hidden rounded-xl ring-1 ring-[#E5E0CC] hover:ring-2 hover:ring-[#F59E0B] transition group"
              >
                <img src={g.image_url} alt={g.caption || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </button>
            ))}
          </div>
        )}

        {lb >= 0 && visible[lb] && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLb(-1)} data-testid="jirt-gallery-lightbox">
            <button className="absolute top-4 right-4 text-white" onClick={() => setLb(-1)} aria-label="Close" data-testid="jirt-gallery-lightbox-close"><X size={28} /></button>
            <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl">
              <img src={visible[lb]?.image_url} alt={visible[lb]?.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
              {visible[lb]?.caption && <figcaption className="mt-3 text-center text-white/90 text-sm">{visible[lb].caption}</figcaption>}
            </figure>
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
//  ACADEMICS PAGE
// ============================================================
export function JirtAcademics() {
  const data = useJirt();
  const docs = data.academics || [];
  return (
    <div data-testid="jirt-academics">
      <section className="bg-gradient-to-br from-[#0F5132] to-[#065F46] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FDE68A] font-bold">Academics · शैक्षणिक</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>शैक्षणिक दस्तऐवज</h1>
          <p className="mt-4 max-w-2xl text-white/85">अभ्यासक्रम, वेळापत्रक, नियम — डाउनलोडसाठी उपलब्ध.</p>
        </div>
      </section>
      <PublicAcademicsPage docs={docs} theme="saffron" testidPrefix="jirt-ac" />
    </div>
  );
}

// ============================================================
//  CONTACT PAGE
// ============================================================
export function JirtContact() {
  const data = useJirt();
  const inst = data.institute || {};
  const depts = data.departments || [];
  const [form, setForm] = useState({ candidate_name: "", mobile: "", email: "", department_id: "", inquiry_details: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.candidate_name.trim()) return toast.error("कृपया नाव लिहा.");
    const mobile = form.mobile.replace(/\D/g, "");
    if (mobile.length !== 10) return toast.error("मोबाईल १० अंकी असावा.");
    setBusy(true);
    try {
      await api.post(`/public/admission-inquiry/${SLUG}`, {
        candidate_name: form.candidate_name.trim(), mobile, email: form.email.trim(),
        department_id: form.department_id || "", inquiry_details: form.inquiry_details.trim(),
      });
      toast.success("तुमची चौकशी पाठविली गेली. आम्ही लवकरच संपर्क साधू.");
      setForm({ candidate_name: "", mobile: "", email: "", department_id: "", inquiry_details: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "त्रुटी आली. कृपया पुन्हा प्रयत्न करा.");
    } finally { setBusy(false); }
  };
  const inputCls = "w-full border border-[#E5E0CC] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:border-[#F59E0B]";

  return (
    <div data-testid="jirt-contact">
      <section className="bg-gradient-to-br from-[#0F5132] to-[#065F46] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FDE68A] font-bold">Contact Us · संपर्क व प्रवेश चौकशी</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>संपर्क</h1>
          <p className="mt-3 max-w-2xl text-white/85">प्रवेशासाठी संपर्क करा. आम्ही लवकरच तुमच्याशी संवाद साधू.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-[1fr_1.3fr] gap-8">
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E0CC] rounded-2xl p-5">
            <MapPin className="text-[#F59E0B]" size={24} />
            <div className="mt-3 font-extrabold text-[#0F5132]">{inst.name || "जे.आय.आर.टी. तंत्र प्रशाला वाशी"}</div>
            <div className="mt-1 text-sm text-[#5b5650]" data-testid="jirt-contact-address">
              {[inst.address1, inst.address2, [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "), inst.state].filter(Boolean).join(", ") || "वाशी ता-वाशी, जि-धाराशिव – ४१३५०३"}
            </div>
          </div>
          <div className="bg-white border border-[#E5E0CC] rounded-2xl p-5">
            <Mail className="text-[#F59E0B]" size={24} />
            <div className="mt-3 font-extrabold text-[#0F5132]">ईमेल</div>
            <a href={`mailto:${inst.email || "HMJIRT1234@GMAIL.COM"}`} className="mt-1 block text-sm text-[#5b5650] hover:text-[#F59E0B] break-all" data-testid="jirt-contact-email">{inst.email || "HMJIRT1234@GMAIL.COM"}</a>
          </div>
          {(inst.contact_phone || inst.admission_phone) && (
            <div className="bg-white border border-[#E5E0CC] rounded-2xl p-5">
              <Phone className="text-[#F59E0B]" size={24} />
              <div className="mt-3 font-extrabold text-[#0F5132]">दूरध्वनी</div>
              <div className="mt-1 text-sm text-[#5b5650]" data-testid="jirt-contact-phone">{[inst.admission_phone, inst.contact_phone].filter(Boolean).join(" / ")}</div>
            </div>
          )}

          {/* Staff Contact Roster */}
          <div className="bg-gradient-to-br from-[#FDE68A]/40 to-[#D1FAE5]/50 border-2 border-[#F59E0B]/30 rounded-2xl p-5" data-testid="jirt-contact-roster">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#B45309]">Staff Contacts</div>
            <div className="mt-1 text-[#0F5132] font-extrabold">कर्मचारी संपर्क</div>
            <ul className="mt-3 space-y-3 text-sm">
              {CONTACT_ROSTER.map((p) => (
                <li key={p.phone} className="flex items-start gap-3" data-testid={`jirt-roster-${p.phone}`}>
                  <div className="w-8 h-8 rounded-full bg-[#0F5132] text-[#FDE68A] flex items-center justify-center shrink-0 mt-0.5"><Users size={14} /></div>
                  <div className="min-w-0">
                    <div className="font-bold text-[#0F5132]">{p.name}</div>
                    <a href={`tel:${p.phone}`} className="text-xs text-[#5b5650] hover:text-[#F59E0B] block">{p.phone}</a>
                    <a href={`mailto:${p.email}`} className="text-[11px] text-[#5b5650] hover:text-[#F59E0B] break-all block">{p.email}</a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <div className="bg-white border-2 border-[#E5E0CC] rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-extrabold text-[#0F5132]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>प्रवेश चौकशी फॉर्म</h2>
            <p className="mt-1 text-sm text-[#5b5650]">तुमची माहिती भरा — आमचा कर्मचारी संपर्क साधेल.</p>
            <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-4">
              <label className="block sm:col-span-2"><div className="text-xs font-bold uppercase tracking-wider text-[#0F5132] mb-1">विद्यार्थ्याचे संपूर्ण नाव *</div><input type="text" maxLength={100} value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} className={inputCls} required data-testid="jirt-form-name" /></label>
              <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-[#0F5132] mb-1">मोबाईल क्रमांक *</div><input type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} className={inputCls} placeholder="१० अंकी" required data-testid="jirt-form-mobile" /></label>
              <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-[#0F5132] mb-1">ईमेल (पर्यायी)</div><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="jirt-form-email" /></label>
              <label className="block sm:col-span-2"><div className="text-xs font-bold uppercase tracking-wider text-[#0F5132] mb-1">इयत्ता / अभ्यासक्रम</div>
                <select value={form.department_id} onChange={(e) => set("department_id", e.target.value)} className={inputCls} data-testid="jirt-form-course">
                  <option value="">— निवडा —</option>
                  {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2"><div className="text-xs font-bold uppercase tracking-wider text-[#0F5132] mb-1">चौकशी तपशील</div><textarea rows={4} maxLength={500} value={form.inquiry_details} onChange={(e) => set("inquiry_details", e.target.value)} className={`${inputCls} resize-y`} placeholder="तुमची चौकशी इथे लिहा…" data-testid="jirt-form-details" /></label>
              <button type="submit" disabled={busy} className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-60 text-[#0F5132] hover:text-white font-bold px-6 py-3 rounded-md shadow-md transition-colors" data-testid="jirt-form-submit">{busy ? (<><Loader2 size={16} className="animate-spin" /> पाठवित आहे…</>) : (<><Send size={16} /> चौकशी पाठवा</>)}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================
//  SCROLLER BAR
// ============================================================
function JirtScrollerBar({ items }) {
  const list = (items || []).filter((s) => (s.text || "").trim());
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-4">
        <span className="opacity-80 mr-2 align-middle">▸</span>{s.text}
      </span>
    );
    if (s.url) {
      return (
        <a key={`${s.id}-${i}`} href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline focus:underline outline-none" data-testid={`jirt-scroller-link-${s.id}`}>{inner}</a>
      );
    }
    return <span key={`${s.id}-${i}`} data-testid={`jirt-scroller-text-${s.id}`}>{inner}</span>;
  };
  const duration = Math.max(20, Math.min(60, list.length * 14));
  return (
    <div className="bg-gradient-to-r from-[#F59E0B] to-[#0F5132] text-white border-y border-[#0F5132]/40 overflow-hidden" data-testid="jirt-scroller-bar">
      <style>{`
        @keyframes jirt-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        [data-testid="jirt-scroller-bar"]:hover [data-testid="jirt-scroller-track"] { animation-play-state: paused !important; }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded-sm shrink-0">सूचना</span>
        <div className="flex-1 overflow-hidden">
          <span data-testid="jirt-scroller-track" className="inline-block whitespace-nowrap text-sm font-medium" style={{ animation: `jirt-marquee ${duration}s linear infinite` }}>
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}
